import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  FilterChangedEvent,
  GetRowIdParams,
  GridReadyEvent,
  ModuleRegistry,
  GetContextMenuItemsParams,
  MenuItemDef,
  PaginationChangedEvent,
} from '@ag-grid-community/core'
import { AgGridReact } from '@ag-grid-community/react'
import { MasterDetailModule } from '@ag-grid-enterprise/master-detail'
import { ServerSideRowModelModule } from '@ag-grid-enterprise/server-side-row-model'
import { MenuModule } from '@ag-grid-enterprise/menu'
import styled from 'styled-components'
import { toast } from 'react-toastify'

import { SettingOutlined } from '@ant-design/icons'
import { Button, Checkbox, Input, Select } from '@surecompanies/core_components'
import { Stack } from '@sureco/design-system'
import { browserHistory } from 'react-router'
import { useRouterContext } from 'components/RouterContext'
import BreadcrumbNav from 'enrollment-platform/components/Breadcrumbs'
import { useSearchParams } from 'hooks/useSearchParams'
import {
  BENEFIT_INTEGRATIONS,
  ideonConfigPath,
  noyoIssuesPath,
} from 'enrollment-platform/pages/BenefitIntegrations/paths'
import Page from 'layouts/Page'
import { useBenefitIntegrationsData } from './hooks/useBenefitIntegrationsData'
import { LoadingOverlay } from './components/LoadingOverlay'
import {
  getMainColumnDefs,
  getDetailColumnDefs,
} from './config/columnDefinitions'
import { FilterModel, PartnerBenefitEvent, Task } from './types'
import pbgClient, {
  PartnerEnum,
} from 'enrollment-platform/api/partner-benefit-graph/client'

ModuleRegistry.registerModules([
  ServerSideRowModelModule,
  MasterDetailModule,
  MenuModule,
])

const GridContainer = styled.div`
  position: relative;
  height: 700px;
  width: 100%;

  &.ag-theme-quartz {
    /* Fix for auto-height issue generating empty space */
    .ag-center-cols-viewport {
      min-height: unset !important;
    }
  }
`

const ErrorMessage = styled.div`
  padding: 16px;
  margin: 16px 0;
  background-color: #fee;
  border: 1px solid #fcc;
  border-radius: 4px;
  color: #c33;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const ErrorButton = styled.button`
  background: none;
  border: none;
  color: #c33;
  cursor: pointer;
  text-decoration: underline;
  font-size: 14px;

  &:hover {
    color: #a11;
  }
`

const PAGINATION_PAGE_SIZES = [10, 20, 50, 100]
const DEFAULT_PAGE_SIZE = 20
const LIST_PATH = `/${BENEFIT_INTEGRATIONS}`

type ListParams = {
  attention?: string
  employeePublicKey?: string
  partnerBenefitName?: string
  personFirstName?: string
  personLastName?: string
  taskStatus?: string
  partnerSyncStatus?: string
  page?: string
  pageSize?: string
}

const PARTNER_VALUES = Object.values(PartnerEnum)
const PARTNER_OPTIONS = [
  { value: '', label: 'All' },
  ...PARTNER_VALUES.map((value) => ({ value, label: value })),
]

function isPartnerValue(value?: string) {
  return Boolean(value && PARTNER_VALUES.includes(value as PartnerEnum))
}

function parsePageSize(raw?: string) {
  const n = Number(raw)
  return PAGINATION_PAGE_SIZES.includes(n) ? n : DEFAULT_PAGE_SIZE
}

function parsePage(raw?: string) {
  const n = Number(raw)
  return Number.isInteger(n) && n >= 1 ? n : 1
}

export function filterModelFromUrlParams(params: ListParams) {
  const model: Record<string, object> = {}
  if (params.personFirstName) {
    model.personFirstName = {
      filterType: 'text',
      type: 'contains',
      filter: params.personFirstName,
    }
  }
  if (params.personLastName) {
    model.personLastName = {
      filterType: 'text',
      type: 'contains',
      filter: params.personLastName,
    }
  }
  if (params.taskStatus) {
    model.taskStatus = {
      filterType: 'set',
      values: params.taskStatus.split(','),
    }
  }
  if (params.partnerSyncStatus) {
    model.partnerSyncStatus = {
      filterType: 'set',
      values: params.partnerSyncStatus.split(','),
    }
  }
  return model
}

export function columnParamsFromFilterModel(filterModel: FilterModel = {}) {
  return {
    personFirstName: filterModel.personFirstName?.filter || undefined,
    personLastName: filterModel.personLastName?.filter || undefined,
    taskStatus:
      filterModel.taskStatus?.values && filterModel.taskStatus.values.length > 0
        ? filterModel.taskStatus.values.join(',')
        : undefined,
    partnerSyncStatus:
      filterModel.partnerSyncStatus?.values &&
      filterModel.partnerSyncStatus.values.length > 0
        ? filterModel.partnerSyncStatus.values.join(',')
        : undefined,
  }
}

const ListBenefitIntegrations: React.FC = () => {
  const { router } = useRouterContext()
  const [gridApi, setGridApi] = useState<GridReadyEvent | null>(null)
  const { params, setParams } = useSearchParams<ListParams>()
  const attention = params.attention === '0' ? '0' : '1'
  const employeePublicKey = params.employeePublicKey?.trim() || undefined
  const partnerBenefitName = isPartnerValue(params.partnerBenefitName)
    ? params.partnerBenefitName
    : undefined
  const pageSize = parsePageSize(params.pageSize)
  const page = parsePage(params.page)
  const [epkDraft, setEpkDraft] = useState(params.employeePublicKey ?? '')
  const { isLoading, error, datasource, clearError } =
    useBenefitIntegrationsData({
      attention,
      employeePublicKey,
      partnerBenefitName,
    })

  useEffect(() => {
    setEpkDraft(params.employeePublicKey ?? '')
  }, [params.employeePublicKey])

  useEffect(() => {
    if (browserHistory.getCurrentLocation().pathname !== LIST_PATH) return
    const nextAttention =
      params.attention === '0' || params.attention === '1'
        ? params.attention
        : '1'
    const nextPage = String(parsePage(params.page))
    const nextSize = String(parsePageSize(params.pageSize))
    const partnerIsInvalid = Boolean(
      params.partnerBenefitName && !isPartnerValue(params.partnerBenefitName)
    )
    if (
      params.attention !== nextAttention ||
      params.page !== nextPage ||
      params.pageSize !== nextSize ||
      partnerIsInvalid
    ) {
      setParams(
        {
          attention: nextAttention,
          page: nextPage,
          pageSize: nextSize,
          ...(partnerIsInvalid ? { partnerBenefitName: undefined } : {}),
        },
        { replace: true }
      )
    }
  }, [
    params.attention,
    params.page,
    params.pageSize,
    params.partnerBenefitName,
    setParams,
  ])

  const columnDefs = useMemo(() => getMainColumnDefs(), [])
  const detailColumnDefs = useMemo(() => getDetailColumnDefs(), [])

  const resyncTask = useCallback(
    async (taskId: string) => {
      try {
        await pbgClient.post(`/tasks/${taskId}/execute`)
        toast.success('Task resync initiated successfully!', {
          position: 'top-right',
        })

        // Refresh the grid data
        if (gridApi?.api) {
          gridApi.api.refreshServerSide({ purge: true })
        }
      } catch (error: any) {
        console.error('Error resyncing task:', error)
        const errorMessage =
          error.response?.data?.message || 'Failed to resync task'
        toast.error(`Resync failed: ${errorMessage}`, {
          position: 'top-right',
        })
      }
    },
    [gridApi]
  )

  const getContextMenuItems = useCallback(
    (params: GetContextMenuItemsParams<Task>): (string | MenuItemDef)[] => {
      if (!params.node?.data) {
        return []
      }

      const task = params.node.data as Task

      return [
        {
          name: 'Resync Task',
          action: () => resyncTask(task.id),
          icon: '<span class="ag-icon ag-icon-refresh"></span>',
        },
        'separator',
        'copy',
        'copyWithHeaders',
        'export',
      ]
    },
    [resyncTask]
  )

  const handleGridReady = useCallback(
    (event: GridReadyEvent) => {
      setGridApi(event)
      if (page > 1) {
        event.api.paginationGoToPage(page - 1)
      }
    },
    [page]
  )

  const handleClearFilter = useCallback(() => {
    setParams(
      { attention: '0', page: '1', pageSize: String(pageSize) },
      { reset: true }
    )
    if (gridApi?.api) {
      gridApi.api.setFilterModel(null)
      gridApi.api.refreshServerSide({ purge: true })
    }
  }, [gridApi, pageSize, setParams])

  const handleEpkSearch = useCallback(
    (value: string) => {
      setParams({
        employeePublicKey: value.trim() || undefined,
        page: '1',
      })
      if (gridApi?.api) {
        gridApi.api.refreshServerSide({ purge: true })
      }
    },
    [gridApi, setParams]
  )

  const handlePartnerChange = useCallback(
    (value: string) => {
      setParams({
        partnerBenefitName: value || undefined,
        page: '1',
      })
      if (gridApi?.api) {
        gridApi.api.refreshServerSide({ purge: true })
      }
    },
    [gridApi, setParams]
  )

  const handleAttentionChange = useCallback(
    (event: { target: { checked: boolean } }) => {
      setParams({
        attention: event.target.checked ? '1' : '0',
        page: '1',
      })
      if (gridApi?.api) {
        gridApi.api.refreshServerSide({ purge: true })
      }
    },
    [gridApi, setParams]
  )

  const handleFilterChanged = useCallback(
    (event: FilterChangedEvent) => {
      const next = columnParamsFromFilterModel(event.api.getFilterModel())
      const current = {
        personFirstName: params.personFirstName || undefined,
        personLastName: params.personLastName || undefined,
        taskStatus: params.taskStatus || undefined,
        partnerSyncStatus: params.partnerSyncStatus || undefined,
      }
      if (
        next.personFirstName === current.personFirstName &&
        next.personLastName === current.personLastName &&
        next.taskStatus === current.taskStatus &&
        next.partnerSyncStatus === current.partnerSyncStatus
      ) {
        return
      }
      setParams({ ...next, page: '1' })
    },
    [
      params.personFirstName,
      params.personLastName,
      params.taskStatus,
      params.partnerSyncStatus,
      setParams,
    ]
  )

  const handlePaginationChanged = useCallback(
    (event: PaginationChangedEvent) => {
      const nextPage = String(event.api.paginationGetCurrentPage() + 1)
      const nextSize = String(event.api.paginationGetPageSize())
      if (nextPage === String(page) && nextSize === String(pageSize)) {
        return
      }
      setParams({ page: nextPage, pageSize: nextSize })
    },
    [page, pageSize, setParams]
  )

  const getRowId = useCallback((params: GetRowIdParams<Task>) => {
    return params.data.id
  }, [])

  const isRowMaster = useCallback((dataItem: Task) => {
    return Boolean(dataItem.partnerBenefitEvent?.length)
  }, [])

  const getDetailRowData = useCallback((params: any) => {
    const events = [...(params.data.partnerBenefitEvent || [])].sort(
      (a: PartnerBenefitEvent, b: PartnerBenefitEvent) => {
        if (a.createdTimestamp !== b.createdTimestamp) {
          return a.createdTimestamp < b.createdTimestamp ? 1 : -1
        }
        return Number(b.id) - Number(a.id)
      }
    )
    params.successCallback(events)
  }, [])

  useEffect(() => {
    if (gridApi?.api) {
      gridApi.api.setGridOption('serverSideDatasource', datasource)
    }
  }, [gridApi, datasource])

  useEffect(() => {
    if (!gridApi?.api) return
    gridApi.api.setFilterModel(filterModelFromUrlParams(params))
  }, [
    gridApi,
    params.personFirstName,
    params.personLastName,
    params.taskStatus,
    params.partnerSyncStatus,
  ])

  const breadcrumbItems = useMemo(
    () => [{ title: 'Home', path: '/' }, { title: 'Benefit Integrations' }],
    []
  )

  const detailCellRendererParams = useMemo(
    () => ({
      detailGridOptions: {
        columnDefs: detailColumnDefs,
        defaultColDef: {
          sortable: true,
          resizable: true,
          flex: 1,
        },
      },
      getDetailRowData,
    }),
    [detailColumnDefs, getDetailRowData]
  )

  const defaultColDef = useMemo(
    () => ({
      flex: 1,
      minWidth: 100,
      sortable: true,
      filter: true,
      resizable: true,
    }),
    []
  )

  return (
    <Page
      title="Benefit Integrations"
      maxWidth="100%"
      breadcrumb={<BreadcrumbNav items={breadcrumbItems} />}
    >
      {error && (
        <ErrorMessage role="alert">
          <span>Error loading data: {error}</span>
          <ErrorButton onClick={clearError} aria-label="Dismiss error">
            Dismiss
          </ErrorButton>
        </ErrorMessage>
      )}

      <Stack
        direction="row"
        space={12}
        style={{
          marginBottom: 16,
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Stack direction="row" space={12} style={{ alignItems: 'center' }}>
          <Input.Search
            allowClear
            aria-label="Employee public key"
            placeholder="Employee public key"
            value={epkDraft}
            onChange={(event) => setEpkDraft(event.target.value)}
            onSearch={handleEpkSearch}
            style={{ width: 260 }}
          />
          <Select
            aria-label="Partner"
            value={partnerBenefitName ?? ''}
            onChange={handlePartnerChange}
            options={PARTNER_OPTIONS}
            style={{ width: 200 }}
          />
          <Checkbox
            checked={attention === '1'}
            onChange={handleAttentionChange}
          >
            Needs attention
          </Checkbox>
          <Button onClick={handleClearFilter}>Clear filter</Button>
        </Stack>
        <Stack direction="row" space={8}>
          <Button
            onClick={() =>
              router.push({ pathname: noyoIssuesPath(), query: {} })
            }
          >
            Noyo Issues
          </Button>
          <Button
            icon={<SettingOutlined aria-hidden="true" />}
            onClick={() =>
              router.push({ pathname: ideonConfigPath(), query: {} })
            }
          >
            Ideon Config
          </Button>
        </Stack>
      </Stack>

      <GridContainer className="ag-theme-quartz">
        <AgGridReact<Task>
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          enableCellTextSelection
          masterDetail
          detailCellRendererParams={detailCellRendererParams}
          detailRowAutoHeight
          isRowMaster={isRowMaster}
          getRowId={getRowId}
          onGridReady={handleGridReady}
          onFilterChanged={handleFilterChanged}
          onPaginationChanged={handlePaginationChanged}
          rowModelType="serverSide"
          pagination
          paginationPageSize={pageSize}
          cacheBlockSize={pageSize}
          paginationPageSizeSelector={PAGINATION_PAGE_SIZES}
          suppressPaginationPanel={false}
          suppressRowClickSelection
          suppressCellFocus={false}
          suppressAnimationFrame={false}
          suppressColumnVirtualisation={false}
          suppressRowVirtualisation={false}
          suppressLoadingOverlay={true}
          getContextMenuItems={getContextMenuItems}
          allowContextMenuWithControlKey={true}
        />

        <LoadingOverlay
          isVisible={isLoading}
          message="Loading benefit integrations..."
        />
      </GridContainer>
    </Page>
  )
}

export default ListBenefitIntegrations
